/* Core & libraries */
import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "react-jss";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import {Link} from "react-router-dom";
import classnames from "classnames";

/* Helpers, constants, custom methods, internal libraries */
import { useUserContext } from '../../contexts/userContext';
import { AIRLINES } from '../../constants/appConstants';
import {
  ADMIN_COMPANY_DETAILS_ROUTE,
  TRAVELER_DETAILS_ROUTE,
  ADMIN_TRAVELER_DETAILS_ROUTE,
  GUEST_DETAILS_ROUTE
} from "../../constants/routes";
import { colors } from "../../constants/colors";
import { nameInitials } from "../../helpers/general.js";

/* Components */
import {ReactComponent as SearchIcon} from "../../resources/images/search.svg";
import {ReactComponent as Close} from "../../resources/images/close-icon.svg";
import {ReactComponent as ClearIcon} from "../../resources/images/thin-close-icon.svg";
import UCInput from "../ucinput";
import UCButton from "../buttons";
import UCCheckbox from "../uccheckbox"
import UCOutsideClickHandler from "../ucOusideClickHandler/UCOutsideClickHandler";
import Spinner from "../spinners/buttonSpinner";

/* Styles */
import styles from "./styles.js"

const messages = defineMessages({
  searchPlaceholder: {
    id: "peopleGrid.search",
  },
  searchNameOrEmailPlaceholder: {
    id: "peopleGrid.search.nameOrEmail",
  },
});

const UCAutocomplete = props => {
  const {
    options,
    getOptions,
    onClear,
    onSelectOption,
    type,
    icon,
    optionsStyling,
    inputStyling,
    placeholder,
    inputWrapperStyling,
    removableChip,
    setRemovableChip,
    disabled,
    recentOptions,
    errorResponse,
    assignSelected,
    className,
    showMinError = true,
    value,
    noResultsMessage,
    shouldFetchOptionsProp = true,
    spinnerName,
    ariaLabel,
    tabIndex,
    resultTotal,
  } = props;

  const theme = useTheme();
  const classes = styles({theme});
  const intl = useIntl();
  const inputRef = useRef(null);
  const companiesContainerRef = useRef(null);
  const companiesPageRef = useRef(1);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const isQueryChangedRef = useRef(false);

  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState(value ?? "");
  const [showOptions, setShowOptions] = useState(false);
  const [shouldFetchOptions, setShouldFetchOptions] = useState(shouldFetchOptionsProp);
  const [accumulatedOptions, setAccumulatedOptions] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const showRecentUsers = recentOptions && query.length === 0;
  const showSearchedOptions = (type === "COMPANIES" || type === "PEOPLE_AADMIN" || type === "COMPANIES_LEP" || type === "PEOPLE" || type === "GUEST") ? options && query.length > 0 : options && query.length > 2;
  const isPaginatedType = type === "COMPANIES" || type === "COMPANIES_LEP";
  const displayedOptions = isPaginatedType ? accumulatedOptions : options;

  const userContext = useUserContext();
  const providerCode = userContext?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    if (isPaginatedType && options) {
      isQueryChangedRef.current = false;
      if (companiesPageRef.current === 1) {
        hasMoreRef.current = true;
        setAccumulatedOptions(options);
      } else {
        if (options.length === 0) {
          hasMoreRef.current = false;
        } else {
          setAccumulatedOptions(prev => [...prev, ...options]);
        }
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [options]);

  useEffect(() => {
    if (shouldFetchOptions) {

      const timeOutId = setTimeout(() => {
        setQuery(inputValue)
        if (inputValue) {
          if(spinnerName){
            getOptions(inputValue, spinnerName)
          } else getOptions(inputValue)
        }
      }, 300);
      return () => clearTimeout(timeOutId);
    }
  }, [inputValue, shouldFetchOptions]);

  const onChangeInput = event => {
    setInputValue(event.target.value)
    setShouldFetchOptions(true);
    if (isPaginatedType) resetCompaniesScroll();
    if (!event.target.value && typeof onClear === 'function') onClear();
  }

  const onClickOption = item => {
    onSelectOption(item)
    setShowOptions(false)
  }

  const onClickPolicyOption = item => {
    onSelectOption(item)
    setInputValue(item.name)
    setShowOptions(false)
    setShouldFetchOptions(false)
  }

  const onClickDetailedOption = item => {
    setRemovableChip(item)
    onSelectOption(item)
    setShowOptions(false)
  }

  const onClickAssignOption = item => {
    onSelectOption(item)
    assignSelected.length > 48 && setShowOptions(false)
  }

  const onClickAssignCompanyOption = item => {
    onSelectOption(item)
    assignSelected.length > 23 && setShowOptions(false)
  }

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ""
      setInputValue("")
      setQuery("");
      setShouldFetchOptions(true);
      if (typeof props.onClear === "function") {
        props.onClear();
      }
    }
    if (typeof onClear === 'function') onClear();
  }

  const renderOption = (elem, index) => {
    switch (type) {
      case "PEOPLE":
        return (
          <li key={elem.userEntityId} onClick={() => onClickOption(elem)}>
            <Link to={`${TRAVELER_DETAILS_ROUTE.replace(":travelerId", elem.userEntityId)}`} className={classes.link}
                  state={{userRowDetails: elem}}>
              <div className={classes.personInfo}>
                <span className={classes.name}>{`${elem.firstName} ${elem.lastName}`}</span>
                {elem?.emailAddress && <span className={classes.personEmail}>{elem.emailAddress}</span>}
              </div>
            </Link>
          </li>
        )
      case "GUEST":
        return (
          <li key={elem.userEntityId} onClick={() => onClickOption(elem)}>
            <Link to={`${GUEST_DETAILS_ROUTE.replace(":guestId", elem.userEntityId)}`} className={classes.link} state={{userRowDetails: elem}}>
              <div className={classes.personInfo}>
                <span className={classes.name}>{`${elem.firstName} ${elem.lastName}`}</span>
                {elem?.emailAddress && <span className={classes.personEmail}>{elem.emailAddress}</span>}
              </div>
            </Link>
          </li>
        )
      case "COMPANIES":
        return (
          <li key={elem.companyEntityId} onClick={() => onClickOption(elem)}>
            <Link to={`${ADMIN_COMPANY_DETAILS_ROUTE.replace(":companyEntityId", elem.companyEntityId)}`}
                  className={classes.link}>
              <div className={classes.companyName}>{`${elem.companyName}`}</div>
              <div className={classes.accountNumber}>{elem?.beanNumber}</div>
            </Link>
          </li>
        )
      case "PEOPLE_DETAILED":
        return (
          <>
            {showRecentUsers && index === 0 &&
              <p className={classes.allUsers}><FormattedMessage id="autocomplete.recentusers"/></p>}
            {showSearchedOptions && index === 0 &&
              <p className={classes.allUsers}><FormattedMessage id="autocomplete.allusers"/></p>}
            <li 
              key={elem.userEntityID} 
              onClick={() => onClickDetailedOption(elem)} 
              className={classes.detailedList} tabIndex={0} 
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClickDetailedOption(elem)
                }
              }}
            >
              <div className={classes.profile}>
                <div className={classes.profileAvatar}>{nameInitials( `${elem.firstName} ${elem.lastName}`)}</div>
                <div className={classes.profileDetails}>
                  <p className={classes.name}> {`${elem.firstName} ${elem.lastName}`}</p>
                  <p className={classes.email}>{elem.emailAddress}</p>
                </div>
              </div>
            </li>
          </>
        )
      case "ASSIGN_LEP":
        return (
          <>
            <li key={elem.userEntityId}>
              <UCCheckbox
                checked={assignSelected.some(user => user.userEntityId === elem.userEntityId)}
                onChange={() => onClickAssignOption(elem)}
                label={`${elem.firstName} ${elem.lastName}`}
              />
            </li>
          </>
        );
      case "COMPANIES_LEP":
        return (
          <>
            <li key={elem.companyEntityId}>
              <UCCheckbox
                checked={assignSelected.some(user => user.companyEntityId === elem.companyEntityId)}
                onChange={() => onClickAssignCompanyOption(elem)}
                label={<>
                <div>{elem.companyName}</div>
                <p>{elem.beanNumber}</p>
                </>}
              />
            </li>
          </>
        );
      case "POLICY":
        return (
          <>
            <li key={elem.id} onClick={() => onClickPolicyOption(elem)}>
              <div className={classes.policyWrapper}>
                <div className={classes.policyBox}>
                  <div>{elem.name}</div>
                  <span>{elem.key}</span>
                </div>
                {!elem.airports && <p>{elem.type && <FormattedMessage id={`specificPolicy.locationType.${elem.type}`}/>}</p>}
              </div>
            </li>
            {elem.airports &&
              elem.airports.map((item, index) => {
                return (
                  <li key={index} onClick={() => onClickPolicyOption(item)}>
                    <div className={classes.policyLocationWrapper}>
                      <div className={classes.policyBox}>
                        <div>{item.name}</div>
                        <span>{item.key}</span>
                      </div>
                      <p>{item.type && <FormattedMessage id={`specificPolicy.locationType.${item.type}`}/>}</p>
                    </div>
                  </li>
                );
              })}
          </>
        );
      case "PEOPLE_AADMIN":
        return (
          <li key={elem.userEntityId} onClick={() => onClickOption(elem)}>
            <Link to={`${ADMIN_TRAVELER_DETAILS_ROUTE.replace(":travelerId", elem.userEntityId)}`} className={classes.link} state={{ userRowDetails: elem }}>
              <div className={classes.companyName}>{`${elem.firstName} ${elem.lastName}`}</div>
              {providerCode !== AIRLINES.AS && <div className={classes.accountNumber}>{elem?.accountNumber}</div>}
              {elem?.companyName && <div className={classes.companyDetails}>{elem?.companyName}</div>}
            </Link>
          </li>
        )
    }
  }

  const handleCompaniesScroll = (e) => {
    if (isLoadingMoreRef.current || !hasMoreRef.current || isQueryChangedRef.current) return;
    if (resultTotal !== undefined && accumulatedOptions.length >= resultTotal) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      companiesPageRef.current += 1;
      getOptions(inputValue, companiesPageRef.current);
    }
  };

  const resetCompaniesScroll = () => {
    companiesPageRef.current = 1;
    hasMoreRef.current = true;
    isLoadingMoreRef.current = false;
    isQueryChangedRef.current = true;
    setAccumulatedOptions([]);
    setIsLoadingMore(false);
    if (companiesContainerRef.current) companiesContainerRef.current.scrollTop = 0;
  };

  const renderOptions = () => {
    if (displayedOptions.length === 0 && !errorResponse) {
      return(
        <>
          {
            noResultsMessage ? noResultsMessage :
              <FormattedMessage id="peopleGrid.search.noResults"/>
          }
        </>
      )
    } else if (isPaginatedType) {
      return (
        <ul>
          {displayedOptions.map((elem, index) => renderOption(elem, index))}
          {isLoadingMore && (
            <li className={classes.spinnerItem}>
              <Spinner size={20} color={theme.spinners["general"]} customClass={classes.invitesSpinner}/>
            </li>
          )}
        </ul>
      );
    } else {
      return (
        <ul>
          {
            options.map((elem, index) => (
                renderOption(elem, index)
              )
            )
          }
        </ul>
      )
    }
  }

  const handleRemoveChip = () => {
    setRemovableChip({});
    setQuery("");
    setInputValue("")
  }

  return (
    <UCOutsideClickHandler className={classnames(classes.autocompleteWrapper, className)}
                           onOutsideClick={() => setShowOptions(false)}>
      <UCInput
        value={inputValue}
        className={inputWrapperStyling ? inputWrapperStyling : classes.search}
        inputClassName={inputStyling}
        icon={icon ? icon : <SearchIcon/>}
        placeholder={placeholder ? placeholder : (type === "PEOPLE" || type === "GUEST") ? intl.formatMessage(messages.searchNameOrEmailPlaceholder) : intl.formatMessage(messages.searchPlaceholder)}
        onChange={onChangeInput}
        onClick={() => setShowOptions(true)}
        onFocus={() => setShowOptions(true)}
        disabled={disabled}
        ariaLabel={ariaLabel}
        name="autocompleteSearch"
        tabIndex={tabIndex}
        autocomplete="off"
        removableChip={
          removableChip && Object.keys(removableChip).length > 0 && (
            <span className={classes.removableChip}>
            <p className={classes.removableChipContent}>
              {`${removableChip.firstName} ${removableChip.lastName}`}
              <UCButton
                onClick={() => handleRemoveChip()}
                className={classes.removeBtn}
                icon={<Close/>}
                tabIndex={0}
              />
            </p>
        </span>
          )}
        spinner={{
          name: spinnerName || "searchGridFilters",
          size: 20,
          color: colors.inactiveGrey,
        }}
        clearButton={
          <UCButton
            onClick={() => clearInput()}
            className={classes.clearBtn}
            icon={<ClearIcon/>}
            iconClassName={classes.clearBtnIcon}
            tabIndex={0}
          />
        }
        showClearButton={inputValue.length > 0 && (removableChip ? Object.keys(removableChip).length === 0 : true)}
        inputRef={inputRef}
      />
      <div aria-live="polite" role="status">
        { showMinError && !errorResponse && ( type !== "COMPANIES" && type !== "PEOPLE_AADMIN" && type !== "PEOPLE" && type !== "GUEST" ) && query.length > 0 && query.length < 3 && (
          <div className={optionsStyling ? optionsStyling : classes.options}>
            <FormattedMessage id="peopleGrid.search.min" />
          </div>
        )}
        { errorResponse && inputValue.length > 0 && (
          <div className={optionsStyling ? optionsStyling : classes.options}>
            <FormattedMessage id="peopleGrid.search.error" />
          </div>
        )}
        {showSearchedOptions && showOptions &&
          <div
            ref={isPaginatedType ? companiesContainerRef : undefined}
            className={optionsStyling ? optionsStyling : classes.options}
            onScroll={isPaginatedType ? handleCompaniesScroll : undefined}
          >
            { renderOptions() }
          </div>
        }
        {showRecentUsers && showOptions &&
          <div className={optionsStyling ? optionsStyling : classes.options}>
            { <ul>{recentOptions.map((elem, index) => (renderOption(elem, index)))}</ul> }
          </div>
        }
      </div>
    </UCOutsideClickHandler>
  )
}

export default UCAutocomplete;
